import { z } from 'zod'

type Message<TOutput> = string | { message?: string } | ((arg: TOutput) => { message?: string })

export function notOneOf<TValues extends Array<any>, TOutput>(values: TValues, message?: Message<TOutput>) {
	function refinement(arg: TOutput, ctx: z.RefinementCtx) {
		if (!values.includes(arg)) return

		const defaultMessage = `Must not be one of: ${values.join(', ')}`

		if (typeof message === 'function') {
			const customMessage = message(arg)
			ctx.addIssue({
				code: 'custom',
				message: customMessage.message ?? defaultMessage,
			})
		} else if (typeof message === 'string') {
			ctx.addIssue({
				code: 'custom',
				message: message ?? defaultMessage,
			})
		} else {
			ctx.addIssue({
				code: 'custom',
				message: message?.message ?? defaultMessage,
			})
		}
	}

	return refinement
}

export function zodEnumFromObjKeys<K extends string>(obj: Record<K, any>): z.ZodEnum<Record<K, K>> {
	const [firstKey, ...otherKeys] = Object.keys(obj) as [K, ...Array<K>]
	return z.enum([firstKey, ...otherKeys]) as unknown as z.ZodEnum<Record<K, K>>
}

export const customErrorMap: z.core.$ZodErrorMap = (issue) => {
	switch (issue.code) {
		case 'invalid_type':
			if (issue.input === undefined || issue.input === null) return { message: `Required` }

			const friendlyTypes: Record<string, string> = {
				string: 'Text',
				number: 'Number',
				boolean: 'True/False',
				date: 'Date',
				bigint: 'Number',
				array: 'List',
				object: 'Object',
				function: 'Function',
				symbol: 'Symbol',
				map: 'Map',
				set: 'Set',
			}

			const expectedType = friendlyTypes[issue.expected] || issue.expected
			return { message: `${expectedType} is required` }

		case 'invalid_format':
			if (issue.format === 'email') return { message: `Invalid email address` }

			if (issue.format === 'url') return { message: `Invalid URL` }

			if (issue.format === 'uuid') return { message: `Invalid ID format` }

			return { message: `Invalid value` }

		case 'too_small':
			if (issue.origin === 'string') {
				if (issue.minimum === 1) return { message: `Required` }

				const inclusive = issue.inclusive ? 'at least' : 'more than'
				return { message: `Must be ${inclusive} ${issue.minimum} characters` }
			}
			if (issue.origin === 'number') {
				const inclusive = issue.inclusive ? 'at least' : 'greater than'
				return { message: `Must be ${inclusive} ${issue.minimum}` }
			}
			if (issue.origin === 'array') {
				const inclusive = issue.inclusive ? 'at least' : 'more than'
				return { message: `Must have ${inclusive} ${issue.minimum} items` }
			}
			return { message: 'Invalid value' }

		case 'too_big':
			if (issue.origin === 'string') {
				const inclusive = issue.inclusive ? 'at most' : 'less than'
				return { message: `Must be ${inclusive} ${issue.maximum} characters` }
			}
			if (issue.origin === 'number') {
				const inclusive = issue.inclusive ? 'at most' : 'less than'
				return { message: `Must be ${inclusive} ${issue.maximum}` }
			}
			if (issue.origin === 'array') {
				const inclusive = issue.inclusive ? 'at most' : 'fewer than'
				return { message: `Must have ${inclusive} ${issue.maximum} items` }
			}
			return { message: 'Invalid value' }

		case 'invalid_value':
			return { message: `Invalid option` }

		case 'invalid_union':
			return { message: `Invalid input` }

		case 'custom':
			return { message: issue.message ?? `Invalid value` }

		default:
			return { message: 'Invalid value' }
	}
}

export const formatErrorWithPercent = (error: string) => {
	return error.replace(/\b(\d+(\.\d+)?)\b/g, (match) => `${match}%`)
}
